// src/components/dashboard/Dashboard.jsx
// ✅ Dashboard autonoma — si adatta ai PERMESSI del ruolo custom
// ✅ Mappa cantieri geolocalizzati integrata
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProjects } from '../../hooks/useProjects';
import { useExpenses } from '../../hooks/useExpenses';
import { ProjectsMap } from '../projects/ProjectMap';
import StatCard from './StatCard';
import RecentActivity from './RecentActivity';
import UsageMeter from '../organization/UsageMeter';
import UpgradeBanner from '../organization/UpgradeBanner';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import {
    Chart as ChartJS, ArcElement, Tooltip, Legend,
    CategoryScale, LinearScale, BarElement, Title,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import {
    PlusIcon, ArrowRightIcon, ExclamationTriangleIcon,
    FolderIcon, CurrencyDollarIcon, ChartBarIcon,
    ClipboardDocumentCheckIcon, WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { EXPENSE_CATEGORY_LABELS } from '../../utils/constants';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const STATUS_LABELS = {
    DRAFT: 'Bozza', PLANNED: 'Pianificato', IN_PROGRESS: 'In Corso',
    ON_HOLD: 'In Attesa', DELAYED: 'Ritardato', COMPLETED: 'Completato', CANCELLED: 'Cancellato',
};
const PIE_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#6B7280'];

const WelcomeHeader = ({ user, organization, effectivePlan, permissions }) => {
    const { t } = useTranslation();
    const h = new Date().getHours();
    const greeting = h < 12 ? t('dashboard.goodMorning') : h < 18 ? t('dashboard.goodAfternoon') : t('dashboard.goodEvening');
    return (
    <div className="bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 right-0 w-72 h-72 bg-blue-400 rounded-full -translate-y-36 translate-x-36" />
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-indigo-400 rounded-full translate-y-28 -translate-x-28" />
        </div>
        <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center text-sm font-bold">IP</div>
                    <span className="text-blue-300 text-sm font-semibold tracking-wide">InfraPilot</span>
                </div>
                <p className="text-slate-400 text-sm">{greeting},</p>
                <h1 className="text-2xl font-bold mt-0.5">{user?.firstName} {user?.lastName}</h1>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <p className="text-blue-200 text-sm">{organization?.name} · Piano <span className="font-semibold">{effectivePlan}</span></p>
                    {/* Badge ruolo standard */}
                    {user?.role && user.role !== 'SUPER_ADMIN' && !permissions?.customRoleName && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                            user.role === 'SITE_MANAGER'     ? 'bg-blue-500/30 text-blue-200' :
                            user.role === 'ENGINEER'         ? 'bg-emerald-500/30 text-emerald-200' :
                            user.role === 'ACCOUNTANT'       ? 'bg-yellow-500/30 text-yellow-200' :
                            user.role === 'FIELD_SUPERVISOR' ? 'bg-orange-500/30 text-orange-200' :
                            'bg-slate-500/30 text-slate-300'
                        }`}>
                            {{ SITE_MANAGER: '🏗️ Site Manager', ENGINEER: '⚙️ Ingegnere',
                               ACCOUNTANT: '💼 Contabile', FIELD_SUPERVISOR: '🔧 Supervisore',
                               VIEWER: '👁️ Visualizzatore' }[user.role] || user.role}
                        </span>
                    )}
                    {permissions?.customRoleName && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{ backgroundColor: permissions.customRoleColor + '33', color: permissions.customRoleColor || '#93c5fd' }}>
                            {permissions.customRoleName}
                        </span>
                    )}
                </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
                {(permissions?.canManageProjects !== false) && (
                    <Link to="/projects/new" data-tour="new-project"
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition">
                        <PlusIcon className="w-4 h-4" /> {t('projects.new')}
                    </Link>
                )}
                {(permissions?.canCreateExpenses !== false) && (
                    <Link to="/expenses/new"
                        className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-medium transition">
                        <PlusIcon className="w-4 h-4" /> {t('expenses.new')}
                    </Link>
                )}
            </div>
        </div>
    </div>
    );
};

const BudgetAlerts = ({ projects }) => {
    const alerts = useMemo(() =>
        projects
            .filter(p => p.estimatedBudget > 0 && (p.actualCost / p.estimatedBudget) >= 0.70)
            .map(p => ({
                project: p,
                pct: ((p.actualCost / p.estimatedBudget) * 100).toFixed(0),
                type: (p.actualCost / p.estimatedBudget) >= 1 ? 'danger'
                    : (p.actualCost / p.estimatedBudget) >= 0.90 ? 'warning' : 'info',
            }))
            .sort((a, b) => b.pct - a.pct),
        [projects]
    );
    if (!alerts.length) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {alerts.slice(0, 4).map((a, i) => (
                <Link key={i} to={`/projects/${a.project.id}`}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border text-sm font-medium transition hover:shadow-sm ${a.type === 'danger' ? 'bg-red-50 border-red-200 text-red-700' :
                        a.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                            'bg-blue-50 border-blue-200 text-blue-700'
                        }`}>
                    <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 truncate">{a.project.name}: budget al {a.pct}%</span>
                    <ArrowRightIcon className="w-3.5 h-3.5 flex-shrink-0" />
                </Link>
            ))}
        </div>
    );
};

const ProjectsSection = ({ projects, currency }) => {
    const { t } = useTranslation();
    return (
    <div className="bg-white rounded-2xl border p-6">
        <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{t('dashboard.activeProjects')}</h3>
            <Link to="/projects" className="text-xs text-blue-600 hover:underline">{t('common.seeAll')}</Link>
        </div>
        <div className="space-y-3">
            {projects.filter(p => ['IN_PROGRESS', 'PLANNED'].includes(p.status)).slice(0, 5).map(p => {
                const pct = p.estimatedBudget > 0 ? Math.min(100, ((p.actualCost || 0) / p.estimatedBudget * 100)).toFixed(0) : 0;
                return (
                    <Link key={p.id} to={`/projects/${p.id}`}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <WrenchScrewdriverIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full">
                                    <div className={`h-full rounded-full ${parseInt(pct) >= 90 ? 'bg-red-500' : parseInt(pct) >= 70 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
                                        style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-gray-400">{pct}%</span>
                            </div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'IN_PROGRESS' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                            {STATUS_LABELS[p.status] || p.status}
                        </span>
                    </Link>
                );
            })}
            {projects.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                    <p className="text-3xl mb-2">🏗️</p>
                    <p className="text-sm">{t('projects.noProjects')}</p>
                </div>
            )}
        </div>
    </div>
    );
};

const ValidationSection = ({ expenses, currency }) => {
    const { t } = useTranslation();
    const pending = expenses.filter(e => !e.validated);
    return (
        <div className="bg-white rounded-2xl border border-yellow-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <ClipboardDocumentCheckIcon className="w-5 h-5 text-yellow-600" />
                    <h3 className="text-sm font-semibold text-gray-700">{t('dashboard.toValidate')} ({pending.length})</h3>
                </div>
                <Link to="/expenses" className="text-xs text-blue-600 hover:underline">{t('common.seeAll')}</Link>
            </div>
            {pending.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{t('dashboard.allOk')}</p>
            ) : (
                <div className="space-y-2">
                    {pending.slice(0, 5).map(e => (
                        <Link key={e.id} to={`/expenses/${e.id}`}
                            className="flex items-center gap-3 p-3 hover:bg-yellow-50 rounded-xl transition">
                            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <CurrencyDollarIcon className="w-4 h-4 text-yellow-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{e.description}</p>
                                <p className="text-xs text-gray-400">{EXPENSE_CATEGORY_LABELS[e.category] || e.category}</p>
                            </div>
                            <span className="text-sm font-bold text-gray-900">{formatCurrency(e.totalAmount, currency)}</span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

const FinancialSection = ({ projects, expenses, currency }) => {
    const { t } = useTranslation();
    const totalSpent = expenses.reduce((s, e) => s + (e.totalAmount || 0), 0);
    const totalBudget = projects.reduce((s, p) => s + (p.estimatedBudget || 0), 0);
    const pctUsed = totalBudget > 0 ? ((totalSpent / totalBudget) * 100).toFixed(1) : 0;
    const byCategory = expenses.reduce((acc, e) => ({ ...acc, [e.category]: (acc[e.category] || 0) + (e.totalAmount || 0) }), {});

    return (
        <div className="bg-white rounded-2xl border p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{t('dashboard.budgetControl')}</h3>
                <Link to="/reports" className="text-xs text-blue-600 hover:underline">{t('reports.title')}</Link>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                    <p className="text-xs text-blue-600 font-medium">{t('dashboard.totalBudget')}</p>
                    <p className="text-sm font-bold text-blue-900 mt-1">{formatCurrency(totalBudget, currency)}</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-xl">
                    <p className="text-xs text-orange-600 font-medium">{t('dashboard.totalExpenses')}</p>
                    <p className="text-sm font-bold text-orange-900 mt-1">{formatCurrency(totalSpent, currency)}</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-xl">
                    <p className="text-xs text-emerald-600 font-medium">{t('dashboard.budgetControl')}</p>
                    <p className="text-sm font-bold text-emerald-900 mt-1">{pctUsed}%</p>
                </div>
            </div>
            <div className="space-y-2">
                {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([cat, amount]) => {
                    const p = totalSpent > 0 ? ((amount / totalSpent) * 100).toFixed(1) : 0;
                    return (
                        <div key={cat} className="flex items-center gap-3">
                            <span className="text-xs text-gray-600 w-24 truncate">{EXPENSE_CATEGORY_LABELS[cat] || cat}</span>
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full">
                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${p}%` }} />
                            </div>
                            <span className="text-xs font-semibold text-gray-700 w-24 text-right">{formatCurrency(amount, currency)}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const GlobalCharts = ({ projects, expenses, currency }) => {
    const { t } = useTranslation();
    const projectsByStatus = projects.reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), {});
    const expensesByCategory = expenses.reduce((acc, e) => ({ ...acc, [e.category]: (acc[e.category] || 0) + (e.totalAmount || 0) }), {});

    const pieData = {
        labels: Object.keys(projectsByStatus).map(s => STATUS_LABELS[s] || s),
        datasets: [{ data: Object.values(projectsByStatus), backgroundColor: PIE_COLORS, borderWidth: 0 }],
    };
    const barData = {
        labels: Object.keys(expensesByCategory).map(c => EXPENSE_CATEGORY_LABELS[c] || c),
        datasets: [{ label: `Spese (${currency})`, data: Object.values(expensesByCategory), backgroundColor: '#3B82F6', borderRadius: 8 }],
    };
    const barOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v, currency) } } },
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border p-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Stato Progetti</h3>
                {Object.keys(projectsByStatus).length > 0 ? (
                    <div className="h-64"><Pie data={pieData} options={{ responsive: true, maintainAspectRatio: false }} /></div>
                ) : (
                    <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                        <p className="text-4xl mb-2">🏗️</p>
                        <p className="text-sm">{t('projects.noProjects')}</p>
                        <Link to="/projects/new" className="mt-3 text-blue-600 text-sm hover:underline">{t('projects.createFirst')} →</Link>
                    </div>
                )}
            </div>
            <div className="bg-white rounded-2xl border p-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Spese per Categoria</h3>
                {Object.keys(expensesByCategory).length > 0 ? (
                    <div className="h-64"><Bar data={barData} options={barOptions} /></div>
                ) : (
                    <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                        <p className="text-4xl mb-2">💰</p>
                        <p className="text-sm">{t('expenses.noExpenses')}</p>
                        <Link to="/expenses/new" className="mt-3 text-blue-600 text-sm hover:underline">Registra la prima →</Link>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==================== MAIN DASHBOARD ====================
const Dashboard = () => {
    const { t } = useTranslation();
    const {
        user, organization, isFree, isStarter, isSuperAdmin,
        usage, effectivePlan, planLimits, permissions, can,
    } = useAuth();

    const currency = organization?.currency || 'EUR';
    const { projects, loading: projectsLoading } = useProjects({ limit: 100 });
    const { expenses, loading: expensesLoading } = useExpenses({ limit: 1000 });

    const totalExpenses = useMemo(() => expenses.reduce((s, e) => s + (e.totalAmount || 0), 0), [expenses]);
    const avgBudget = useMemo(() =>
        projects.length ? projects.reduce((s, p) => s + (p.estimatedBudget || 0), 0) / projects.length : 0,
        [projects]
    );

    const maxProjects = planLimits?.maxProjects;
    const isUnlimited = !maxProjects || maxProjects === -1 || maxProjects >= 999999;
    const utilizzoLabel = `${usage?.projectsCount || 0} / ${isUnlimited ? '∞' : maxProjects}`;

    if (projectsLoading || expensesLoading) return <LoadingSpinner />;

    const commonProps = { projects, expenses, currency };

    // ✅ can() gestisce già SUPER_ADMIN + permessi custom + permessi default per ruolo
    const isFullAccess   = isSuperAdmin;
    const showFinancials = isSuperAdmin || can('canViewFinancials');
    const showValidation = isSuperAdmin || can('canValidateExpenses');
    const showProjects   = isSuperAdmin || can('canManageProjects');
    const showExpenses   = isSuperAdmin || can('canManageExpenses');
    const showGlobalCharts = isSuperAdmin || (can('canViewReports') && can('canViewFinancials'));
    const showReports    = isSuperAdmin || can('canViewReports');
    const showAI         = isSuperAdmin || can('canAccessAI');
    const showMap        = showProjects && projects.some(p => p.latitude && p.longitude);

    return (
        <div className="space-y-6">
            {(isFree && usage?.projectsCount >= 1) && <UpgradeBanner />}
            {(isStarter && planLimits?.maxProjects > 0 && usage?.projectsCount >= planLimits.maxProjects * 0.8) && <UpgradeBanner />}

            <WelcomeHeader user={user} organization={organization} effectivePlan={effectivePlan} permissions={permissions} />

            {showProjects && <BudgetAlerts projects={projects} />}

            <div className={`grid grid-cols-1 gap-5 ${showFinancials ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {showProjects && <StatCard title={t('nav.projects')} value={projects.length} icon="🏗️" color="blue" />}
                {showFinancials && <StatCard title={t('dashboard.totalExpenses')} value={formatCurrency(totalExpenses, currency)} icon="💰" color="green" />}
                {showFinancials && <StatCard title={t('dashboard.totalBudget')} value={formatCurrency(avgBudget, currency)} icon="📊" color="purple" />}
                {isFullAccess && <StatCard title={t('plans.limits')} value={utilizzoLabel} icon="📈" color="orange" />}
                {!showFinancials && showProjects && (
                    <StatCard title={t('projects.statuses.IN_PROGRESS')} value={projects.filter(p => p.status === 'IN_PROGRESS').length} icon="⚙️" color="green" />
                )}
                {showValidation && !showFinancials && (
                    <StatCard title={t('dashboard.toValidate')} value={expenses.filter(e => !e.validated).length} icon="✅" color="orange" />
                )}
            </div>

            {/* ✅ Mappa cantieri geolocalizzati */}
            {showMap && <ProjectsMap projects={projects} />}

            {showGlobalCharts && <GlobalCharts {...commonProps} />}

            {showProjects && !showGlobalCharts && <ProjectsSection {...commonProps} />}
            {showValidation && <ValidationSection {...commonProps} />}
            {showFinancials && !showGlobalCharts && <FinancialSection {...commonProps} />}

            {showReports && !showProjects && !showFinancials && (
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-8 text-center">
                    <ChartBarIcon className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-blue-700 mb-1">{t('reports.title')}</p>
                    <p className="text-xs text-blue-500 mb-4">{t('reports.project')}</p>
                    <Link to="/reports" className="inline-block text-sm bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition">
                        {t('common.view')} →
                    </Link>
                </div>
            )}

            {(isFree || isStarter) && <UsageMeter />}
            {showProjects && <RecentActivity projects={projects} expenses={expenses.slice(0, 5)} />}
        </div>
    );
};

export default Dashboard;